import { CartItemQuantity, CartRule, CartRuleResult } from '../types/models';
import { AddToCartItem, ShopifyCart, ShopifyCartItem } from '../types/servicesShopifyCart';
import { useShopifyCartService } from '../services/shopifyCart';
import { cartRuleVariantParentMaxQuantity } from '../cartRules/variantParentMaxQuantity';
import { cartRuleFreeGift } from '../cartRules/freeGift';
import { cartRuleBundleMaxCartQuantity } from '../cartRules/bundleMaxCartQuantity';
import {
  getProductBySku,
  getVariantIdBySku,
} from '@/features/Product/composables/productLookupHelper';

let isValidatingCart = false;

export const useCartValidation = () => {
  const { updateCart, addToCart, getCart } = useShopifyCartService();

  const toUpdateObject = (items: CartItemQuantity[]) =>
    Object.fromEntries(items.map(({ key, quantity }) => [key, quantity]));

  const applyRuleResult = (
    items: ShopifyCartItem[],
    result: CartRuleResult | null,
  ): ShopifyCartItem[] => {
    if (!result) return items;
    const removedKeys = new Set(result.remove.map(({ key }) => key));
    const updatedQuantities = new Map(result.update.map(({ key, quantity }) => [key, quantity]));
    return items
      .filter((item) => !removedKeys.has(item.key))
      .map((item) =>
        updatedQuantities.has(item.key)
          ? { ...item, quantity: updatedQuantities.get(item.key) as number }
          : item,
      );
  };

  const validateCart = async (cart: ShopifyCart): Promise<ShopifyCart> => {
    if (isValidatingCart) return cart;
    isValidatingCart = true;
    try {
      const cartRules: CartRule[] = [
        cartRuleFreeGift,
        cartRuleVariantParentMaxQuantity,
        cartRuleBundleMaxCartQuantity,
      ];
      const itemsToAdd: AddToCartItem[] = [];
      const itemsToUpdate: CartItemQuantity[] = [];
      const itemsToRemove: CartItemQuantity[] = [];

      let workingItems = cart.items;
      cartRules.forEach((rule) => {
        const result = rule({ ...cart, items: workingItems });
        if (!result) return;
        itemsToAdd.push(...result.add);
        itemsToUpdate.push(...result.update);
        itemsToRemove.push(...result.remove);
        workingItems = applyRuleResult(workingItems, result);
      });

      if (!itemsToAdd.length && !itemsToUpdate.length && !itemsToRemove.length) {
        return cart;
      }

      try {
        if (itemsToRemove.length) {
          await updateCart(toUpdateObject(itemsToRemove));
        }

        if (itemsToUpdate.length) {
          await updateCart(toUpdateObject(itemsToUpdate));
        }

        if (itemsToAdd.length) {
          await addToCart(itemsToAdd);
        }
      } catch (error) {
        console.error('Failed to apply cart rules', error);
      }

      return getCart();
    } finally {
      isValidatingCart = false;
    }
  };

  return {
    validateCart,
    getVariantIdBySku,
    getProductBySku,
  };
};
